import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Download,
  Loader2,
  RefreshCw,
  Search,
  ExternalLink,
  Copy,
  ShieldCheck,
  Award,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatMoney = (amount, currency) => {
  if (amount == null) return "—";
  try {
    const value =
      typeof amount === "string" ? parseFloat(amount) : Number(amount);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency || ""} ${amount}`;
  }
};

const StatusPill = ({ status }) => {
  const s = (status || "unknown").toLowerCase();
  const map = {
    paid: "bg-green-500/10 text-green-400 border-green-500/30",
    unpaid: "bg-slate-500/10 text-slate-400 border-slate-500/30",
    pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    failed: "bg-red-500/10 text-red-400 border-red-500/30",
  };
  const cls = map[s] || map.unpaid;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border font-mono text-[10px] tracking-[0.16em] uppercase ${cls}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {s}
    </span>
  );
};

const productIconFor = (id) => (id === "shield" ? ShieldCheck : Award);

export default function AdminOrders() {
  const { authHeader, apiBase } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | paid | unpaid | failed
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/orders`, {
        headers: authHeader(),
      });
      setOrders(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load orders",
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (filter !== "all" && (o.payment_status || "").toLowerCase() !== filter)
        return false;
      if (!q) return true;
      return [
        o.order_id,
        o.product_name,
        o.customer_email,
        o.payer_email,
        o.customer_name,
        o.payer_name,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [orders, query, filter]);

  const stats = useMemo(() => {
    const paid = orders.filter(
      (o) => (o.payment_status || "").toLowerCase() === "paid",
    );
    const revenue = paid.reduce((sum, o) => {
      const amt =
        typeof o.captured_amount === "string"
          ? parseFloat(o.captured_amount)
          : parseFloat(o.amount || 0);
      return sum + (isNaN(amt) ? 0 : amt);
    }, 0);
    return {
      total: orders.length,
      paid: paid.length,
      unpaid: orders.length - paid.length,
      revenue,
    };
  }, [orders]);

  const exportCsv = () => {
    if (orders.length === 0) return;
    const headers = [
      "created_at",
      "order_id",
      "product_id",
      "product_name",
      "amount",
      "currency",
      "payment_status",
      "status",
      "capture_id",
      "customer_name",
      "customer_email",
      "customer_phone",
      "payer_name",
      "payer_email",
    ];
    const rows = orders.map((o) =>
      headers.map((h) => `"${String(o[h] ?? "").replace(/"/g, '""')}"`).join(","),
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    a.download = `sriman-orders-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Orders CSV downloaded.");
  };

  const copyRef = (id) => {
    navigator.clipboard?.writeText(id);
    toast.success("Order reference copied.");
  };

  return (
    <div data-testid="admin-orders-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <span className="overline">/Admin · Orders</span>
          <h1 className="mt-4 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
            Pre-booking orders
          </h1>
          <p className="mt-3 text-slate-500 text-sm">
            Every PayPal transaction, live from the store — pending, paid or
            failed.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            disabled={loading}
            data-testid="admin-orders-refresh"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white text-sm px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={exportCsv}
            disabled={orders.length === 0}
            data-testid="admin-orders-export"
            className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: stats.total, v: "Total orders", id: "orders-stat-total" },
          { k: stats.paid, v: "Paid", id: "orders-stat-paid" },
          { k: stats.unpaid, v: "Pending / failed", id: "orders-stat-unpaid" },
          {
            k: formatMoney(stats.revenue, "USD"),
            v: "Revenue (USD)",
            id: "orders-stat-revenue",
          },
        ].map((s) => (
          <div
            key={s.v}
            data-testid={s.id}
            className="bg-[#0B132B]/50 border border-white/10 p-6"
          >
            <div className="font-display text-white text-2xl md:text-3xl tracking-tight">
              {s.k}
            </div>
            <div className="mt-2 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
              {s.v}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <input
            data-testid="admin-orders-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by order id, product, customer email or name…"
            className="w-full bg-[#0B132B]/60 border border-white/10 focus:border-red-500/60 focus:outline-none text-white placeholder-slate-500 pl-11 pr-4 py-3 text-sm rounded-sm transition-colors"
          />
        </div>
        <div className="flex items-center gap-1 bg-[#0B132B]/60 border border-white/10 p-1 rounded-sm">
          {["all", "paid", "unpaid", "failed"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-orders-filter-${f}`}
              className={`px-3 py-2 rounded-sm text-xs font-mono tracking-[0.14em] uppercase transition-colors ${
                filter === f
                  ? "bg-red-500 text-white"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 bg-[#0B132B]/40 border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-orders-table">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {[
                  "Received",
                  "Product",
                  "Amount",
                  "Customer",
                  "Order ID",
                  "Status",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <Loader2
                      size={20}
                      className="inline animate-spin text-slate-500"
                    />
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <div className="text-slate-500 text-sm">
                      {orders.length === 0
                        ? "No orders yet. Pre-bookings from /products will appear here."
                        : "No orders match your filter."}
                    </div>
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((o) => {
                  const Icon = productIconFor(o.product_id);
                  return (
                    <tr
                      key={o.order_id}
                      data-testid={`admin-order-row-${o.order_id}`}
                      onClick={() => setSelected(o)}
                      className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 text-slate-400 font-mono text-xs whitespace-nowrap">
                        {formatDate(o.created_at)}
                      </td>
                      <td className="px-6 py-4 text-white">
                        <div className="flex items-center gap-2">
                          <Icon
                            size={14}
                            className="text-red-500"
                            strokeWidth={1.5}
                          />
                          {o.product_name || o.product_id || "—"}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-white whitespace-nowrap">
                        {formatMoney(
                          o.captured_amount || o.amount,
                          o.captured_currency || o.currency,
                        )}
                      </td>
                      <td className="px-6 py-4 text-slate-300">
                        <div className="text-white text-sm">
                          {o.payer_name || o.customer_name || "—"}
                        </div>
                        <div className="text-slate-500 text-xs">
                          {o.payer_email || o.customer_email || ""}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-mono text-xs text-slate-400">
                        <div className="flex items-center gap-2">
                          <span className="truncate max-w-[140px] inline-block">
                            {o.order_id}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyRef(o.order_id);
                            }}
                            className="text-slate-500 hover:text-white"
                            aria-label="Copy order id"
                          >
                            <Copy size={12} />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <StatusPill status={o.payment_status} />
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid="admin-order-drawer"
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-lg bg-[#0B132B] border-l border-white/10 h-full overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Order detail</div>
                <h2 className="mt-3 font-display text-white text-2xl tracking-tight">
                  {selected.product_name}
                </h2>
                <div className="mt-2">
                  <StatusPill status={selected.payment_status} />
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid="admin-order-drawer-close"
                className="text-slate-400 hover:text-white text-sm"
              >
                Close
              </button>
            </div>

            <dl className="mt-8 space-y-5 text-sm">
              <div>
                <dt className="overline mb-1">Order reference</dt>
                <dd className="text-white font-mono text-xs break-all flex items-center gap-2">
                  {selected.order_id}
                  <button
                    onClick={() => copyRef(selected.order_id)}
                    className="text-slate-500 hover:text-white"
                  >
                    <Copy size={12} />
                  </button>
                </dd>
              </div>
              {selected.capture_id && (
                <div>
                  <dt className="overline mb-1">Capture ID</dt>
                  <dd className="text-white font-mono text-xs break-all">
                    {selected.capture_id}
                  </dd>
                </div>
              )}
              <div>
                <dt className="overline mb-1">Amount</dt>
                <dd className="text-white">
                  {formatMoney(
                    selected.captured_amount || selected.amount,
                    selected.captured_currency || selected.currency,
                  )}
                </dd>
              </div>
              <div>
                <dt className="overline mb-1">Received</dt>
                <dd className="text-white">
                  {formatDate(selected.created_at)}
                </dd>
              </div>
              <div>
                <dt className="overline mb-1">PayPal status</dt>
                <dd className="text-white font-mono text-xs uppercase tracking-widest">
                  {selected.status || "—"}
                </dd>
              </div>
              <div>
                <dt className="overline mb-1">Customer</dt>
                <dd className="text-white">
                  {selected.payer_name || selected.customer_name || "—"}
                  {(selected.payer_email || selected.customer_email) && (
                    <div className="text-slate-400 text-xs mt-1">
                      {selected.payer_email || selected.customer_email}
                    </div>
                  )}
                  {selected.customer_phone && (
                    <div className="text-slate-400 text-xs mt-1">
                      {selected.customer_phone}
                    </div>
                  )}
                </dd>
              </div>
              {selected.capture_error && (
                <div>
                  <dt className="overline mb-1 !text-red-500">Capture error</dt>
                  <dd className="text-red-300 text-xs whitespace-pre-wrap leading-relaxed border-l-2 border-red-500/40 pl-4">
                    {selected.capture_error}
                  </dd>
                </div>
              )}
            </dl>

            {(selected.payer_email || selected.customer_email) && (
              <div className="mt-10 flex items-center gap-3">
                <a
                  href={`mailto:${selected.payer_email || selected.customer_email}?subject=Sriman%20Solutions%20pre-booking%20${encodeURIComponent(selected.order_id || "")}`}
                  className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-5 py-3 rounded-sm"
                >
                  <ExternalLink size={14} /> Email customer
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
